import { InitiativeStatusType } from 'types/entities/initiative';
import { css, styled } from 'styled-components';
import { InitiativeSupportStatus } from 'types/entities/initiativeSupport';

const supportBadgeVariantStyles = {
  INTERESTED: css`
    color: ${({ theme }) => theme.colors.secondary800};
    background-color: ${({ theme }) => theme.colors.secondary100};
  `,
  CONFIRMED: css`
    color: ${({ theme }) => theme.colors.green800};
    background-color: ${({ theme }) => theme.colors.green100};
  `,
};

export const TableWrapper = styled.div`
  width: 100%;
`;

export const CardList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${({ theme }) => theme.sizes.lg};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    gap: ${({ theme }) => theme.sizes.md};
  }
`;

export const Card = styled.div<{
  $rowClickable?: boolean;
  $supportStatus?: InitiativeSupportStatus;
}>`
  cursor: ${({ $rowClickable }) => ($rowClickable ? 'pointer' : 'default')};
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: ${({ theme }) => theme.sizes.lg};
  min-width: 0;
  padding: ${({ theme }) => theme.sizes.md};
  overflow: hidden;
  background-color: ${({ theme, $supportStatus }) =>
    $supportStatus === 'CONFIRMED'
      ? theme.colors.green50
      : $supportStatus === 'INTERESTED'
        ? theme.colors.secondary50
        : theme.colors.neutral};
  border: 1px solid
    ${({ theme, $supportStatus }) =>
      $supportStatus === 'CONFIRMED'
        ? theme.colors.green500
        : $supportStatus === 'INTERESTED'
          ? theme.colors.secondary500
          : theme.colors.neutral200};
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(35, 67, 124, 0.06);
  transition:
    box-shadow 0.25s ease,
    transform 0.25s ease,
    border-color 0.25s ease,
    background-color 0.25s ease;

  &:hover {
    border-color: ${({ theme, $rowClickable, $supportStatus }) => {
      if (!$rowClickable) return theme.colors.neutral200;
      if ($supportStatus === 'CONFIRMED') return theme.colors.green500;
      if ($supportStatus === 'INTERESTED') return theme.colors.secondary500;
      return theme.colors.primary500;
    }};
    box-shadow: ${({ $rowClickable }) =>
      $rowClickable
        ? '0 10px 28px rgba(89, 132, 204, 0.14)'
        : '0 2px 12px rgba(35, 67, 124, 0.06)'};
    transform: ${({ $rowClickable }) => ($rowClickable ? 'translateY(-2px)' : 'none')};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: column;
    align-items: stretch;
    padding: 0;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    gap: 0;

    &:hover {
      transform: none;
    }
  }
`;

export const CardImage = styled.div`
  position: relative;
  flex: 0 0 160px;
  width: 160px;
  min-width: 0;
  align-self: stretch;
  border-radius: 12px;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.neutral100};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex: none;
    width: 100%;
    height: 180px;
    align-self: stretch;
    border-radius: 0;
  }
`;

export const CardSupportBadge = styled.span<{ $status: InitiativeSupportStatus }>`
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 3;
  display: inline-flex;
  align-items: center;
  max-width: calc(100% - 16px);
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  line-height: 1.2;
  text-transform: uppercase;
  box-shadow: 0 2px 8px rgba(20, 35, 63, 0.12);

  ${({ $status }) => supportBadgeVariantStyles[$status]}
`;

export const CardCarousel = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background-color: ${({ theme }) => theme.colors.neutral100};
`;

export const CardCarouselImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

export const CardCarouselPlaceholder = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

export const CardCarouselButton = styled.button<{ $side: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 999px;
  cursor: pointer;
  background-color: rgba(255, 255, 255, 0.92);
  color: ${({ theme }) => theme.colors.primary700};
  box-shadow: 0 4px 14px rgba(20, 35, 63, 0.18);
  backdrop-filter: blur(6px);

  ${({ $side }) => ($side === 'left' ? 'left: 6px;' : 'right: 6px;')}

  &:hover {
    background-color: ${({ theme }) => theme.colors.neutral};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary300};
    outline-offset: 2px;
  }

  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }
`;

export const CardContent = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme }) => theme.sizes.md};
  }
`;

export const CardMainColumns = styled.div`
  display: flex;
  flex: 1;
  gap: 0
  min-width: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: column;
    gap: ${({ theme }) => theme.sizes.md};
  }
`;

export const CardLeftColumn = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizes.sm};
  min-width: 0;
`;

export const CardRightColumn = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
`;

export const CardIntro = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizes.sm};
`;

export const CardDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizes.sm};
  height: 100%;
`;

export const CardFooter = styled.div`
  margin-top: auto;
`;

export const CardField = styled.div<{ $presentation?: 'title' | 'lead' | 'detail' | 'highlight' }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $presentation }) => ($presentation === 'detail' ? '2px' : '4px')};
  min-width: 0;
`;

export const CardFieldLabel = styled.span<{
  $presentation?: 'title' | 'lead' | 'detail' | 'highlight';
}>`
  font-size: ${({ theme }) => theme.sizes.sm};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.neutral900};
  line-height: 1.3;
`;

export const CardFieldValue = styled.span<{
  $presentation?: 'title' | 'lead' | 'detail' | 'highlight';
  $clampLines?: number;
}>`
  font-size: ${({ theme }) => theme.sizes.sm};
  font-weight: 400;
  color: ${({ theme }) => theme.colors.neutral700};
  line-height: 1.45;
  word-break: break-word;

  ${({ $clampLines }) =>
    $clampLines != null &&
    `
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: ${$clampLines};
    overflow: hidden;
  `}
`;

export const StatusBadge = styled.span<{ $variant?: InitiativeStatusType }>`
  display: inline-block;
  padding: ${({ theme }) => theme.sizes.xs} ${({ theme }) => theme.sizes.sm};
  border-radius: 999px;
  font-size: ${({ theme }) => theme.sizes['2xs']};
  font-weight: 600;
  width: fit-content;
  background-color: ${({ theme, $variant }) =>
    $variant === 'ACTIVE' ? theme.colors.green100 : theme.colors.red100};
  color: ${({ theme, $variant }) =>
    $variant === 'ACTIVE' ? theme.colors.green700 : theme.colors.red700};
`;

export const Actions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.sizes.xs};
`;

export const Action = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.sizes.xs};
  background-color: ${({ theme }) => theme.colors.neutral50};
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.colors.neutral200};
  padding: ${({ theme }) => theme.sizes.xs} ${({ theme }) => theme.sizes.sm};
  width: 60px;

  &:hover {
    background-color: ${({ theme }) => theme.colors.neutral100};
  }
`;

export const PaginationBar = styled.nav`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.sizes.md};
  margin-top: ${({ theme }) => theme.sizes.md};
  padding-top: ${({ theme }) => theme.sizes.md};
  border-top: 1px solid ${({ theme }) => theme.colors.neutral200};
`;

export const PaginationInfo = styled.span`
  font-size: ${({ theme }) => theme.sizes.sm};
  color: ${({ theme }) => theme.colors.neutral800};
`;

export const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.sizes.sm};
`;

export const PaginationButton = styled.button<{ $disabled?: boolean }>`
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  font-size: ${({ theme }) => theme.sizes.sm};
  font-weight: 600;
  color: ${({ theme, $disabled }) =>
    $disabled ? theme.colors.neutral400 : theme.colors.neutral800};
  background-color: ${({ theme }) => theme.colors.neutral50};
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.colors.neutral200};
  padding: ${({ theme }) => theme.sizes.xs} ${({ theme }) => theme.sizes.md};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.neutral100};
  }
`;
